/**
 * Loads learning content from `CONTENT_API_URL` (default `http://localhost:8001`) during dev /
 * static build instead of requiring
 * a prior `data/json/**` copy. Uses the catalog API:
 * - `GET /v1/:locale/content?type=…` — list
 * - `GET /v1/:locale/content/:id?view=textLinked` — detail with token `o` occurrence ids on `script`
 * - `GET /v1/:locale/content/:id` — detail (SoT envelope or flat `meta` + `segments`; derived via {@link deriveRunningJson})
 * - `GET /v1/interface-meta/:uiLang/:section` — UI-language title/teaser/description overlays
 *
 * Call {@link ensureContentApiHydrated} from async handlers (and Vite prerender `urls()`) before
 * reading content via `content.js`.
 */
import { deriveRunningJson } from "./content-api-derive.js";
import { DEFAULT_STORY_PRERENDER_VOICE } from "./audio.js";
import { CONTENT_I18N_LANGS } from "./locale-registry.js";

const CONTENT_TYPES = /** @type {const} */ (["story", "dialog", "adventure", "scene"]);

/** Locales to mirror against the Content API index (same order as `regenerate-from-content-api`). */
const FETCH_LOCALES = [...CONTENT_I18N_LANGS];

/** Website section keys aligned with Content API `interface-meta` paths. */
export const INTERFACE_META_SECTIONS = /** @type {const} */ ([
  "stories",
  "dialogs",
  "scenes",
  "adventures",
]);

/** @type {Map<string, Map<string, Map<string, Record<string, unknown>>>>} */
const byLocaleTypeId = new Map();

/** @type {Map<string, Map<string, Record<string, unknown>>>} */
const byInterfaceMeta = new Map();

let hydratePromise = null;
let hydrated = false;

export function contentApiOrigin() {
  return String(process.env.CONTENT_API_URL ?? "http://localhost:8001").trim().replace(/\/$/, "");
}

export function isContentApiRuntimeEnabled() {
  return Boolean(contentApiOrigin());
}

/** True after {@link ensureContentApiHydrated} has finished (or no-op when disabled). */
export function isContentApiHydrated() {
  return hydrated;
}

/**
 * @template T
 * @param {T[]} items
 * @param {number} concurrency
 * @param {(item: T, index: number) => Promise<void>} fn
 */
async function poolMap(items, concurrency, fn) {
  let idx = 0;
  const workers = Array.from({ length: Math.min(concurrency, items.length) || 1 }, async () => {
    while (idx < items.length) {
      const i = idx++;
      await fn(items[i], i);
    }
  });
  await Promise.all(workers);
}

/**
 * List pages are fetched once per locale and type. A single dropped connection
 * used to skip the whole catalog, so transient failures are retried.
 * @param {string} url
 * @returns {Promise<{ items?: unknown[]; nextCursor?: string | null } | null>}
 */
async function fetchListPage(url) {
  const attempts = 4;
  for (let attempt = 1; ; attempt++) {
    try {
      const res = await fetch(url);
      if (res.status === 404) return null;
      if (!res.ok) {
        const error = new Error(`Content API list failed ${res.status} ${url}`);
        if (res.status < 500 && res.status !== 429) {
          throw Object.assign(error, { permanent: true });
        }
        throw error;
      }
      return /** @type {{ items?: unknown[]; nextCursor?: string | null }} */ (await res.json());
    } catch (error) {
      if (/** @type {{ permanent?: boolean }} */ (error).permanent || attempt >= attempts) throw error;
      await new Promise((resolve) => setTimeout(resolve, 250 * attempt));
    }
  }
}

/**
 * @param {string} origin
 * @param {string} locale
 * @param {string} type
 */
async function fetchListAllPages(origin, locale, type) {
  /** @type {{ id: string; slug?: string; title?: string; level?: string }[]} */
  const out = [];
  let cursor = "";
  for (;;) {
    const q = new URLSearchParams({ type, limit: "200" });
    if (cursor) q.set("cursor", cursor);
    const url = `${origin}/v1/${encodeURIComponent(locale)}/content?${q}`;
    const data = /** @type {{ items?: typeof out; nextCursor?: string | null }} */ (
      await fetchListPage(url)
    );
    if (!data) return [];
    const items = data.items ?? [];
    for (const raw of items) {
      const row = /** @type {{ id?: string; contentId?: string; slug?: string; title?: string; level?: string }} */ (
        raw
      );
      const nid = String(row.contentId ?? row.id ?? row.content_id ?? "").trim();
      if (!nid) continue;
      out.push({
        id: nid,
        slug: row.slug,
        title: row.title,
        level: row.level,
      });
    }
    const nc = data.nextCursor;
    if (!nc) break;
    cursor = nc;
  }
  return out;
}

/**
 * @param {string} url
 */
async function fetchJsonOk(url) {
  const attempts = 4;
  for (let attempt = 1; ; attempt++) {
    try {
      const res = await fetch(url);
      if (res.ok) return /** @type {Record<string, unknown>} */ (await res.json());
      const error = new Error(`GET ${url} → ${res.status}`);
      if (res.status < 500 && res.status !== 429) throw Object.assign(error, { permanent: true });
      throw error;
    } catch (error) {
      if (/** @type {{ permanent?: boolean }} */ (error).permanent || attempt >= attempts) throw error;
      await new Promise((resolve) => setTimeout(resolve, 250 * attempt));
    }
  }
}

/**
 * @param {string} url
 */
async function fetchJsonOptional(url) {
  try {
    const res = await fetch(url);
    if (res.status === 404) return null;
    if (!res.ok) throw new Error(`GET ${url} → ${res.status}`);
    return /** @type {Record<string, unknown>} */ (await res.json());
  } catch {
    return null;
  }
}

/**
 * Full SoT / structured detail document for any content type.
 * @param {string} origin
 * @param {string} locale
 * @param {string} id
 */
/**
 * @param {string} origin
 * @param {string} locale
 * @param {string} id
 * @param {{ voice?: string }} [opts]
 */
async function fetchContentFull(origin, locale, id, opts = {}) {
  const q = new URLSearchParams({
    view: "textLinked",
    include: "modules,exercises",
  });
  if (Object.prototype.hasOwnProperty.call(opts, "voice")) {
    const voice = opts.voice == null ? "" : String(opts.voice).trim();
    if (voice) q.set("voice", voice);
  } else {
    q.set("voice", DEFAULT_STORY_PRERENDER_VOICE);
  }
  const url = `${origin}/v1/${encodeURIComponent(locale)}/content/${encodeURIComponent(id)}?${q}`;
  return fetchJsonOk(url);
}

function typeBucket(locale, type) {
  if (!byLocaleTypeId.has(locale)) {
    const inner = new Map();
    for (const t of CONTENT_TYPES) inner.set(t, new Map());
    byLocaleTypeId.set(locale, inner);
  }
  const m = /** @type {Map<string, Map<string, Record<string, unknown>>>} */ (byLocaleTypeId.get(locale));
  return /** @type {Map<string, Record<string, unknown>>} */ (m.get(type));
}

function interfaceMetaBucket(uiLang, section) {
  const lang = String(uiLang || "").toLowerCase();
  const sec = String(section || "").toLowerCase();
  if (!byInterfaceMeta.has(lang)) byInterfaceMeta.set(lang, new Map());
  const bySection = /** @type {Map<string, Map<string, Record<string, unknown>>>} */ (
    byInterfaceMeta.get(lang)
  );
  if (!bySection.has(sec)) bySection.set(sec, new Map());
  return /** @type {Map<string, Record<string, unknown>>} */ (bySection.get(sec));
}

/**
 * @param {string} slugHint
 * @param {Record<string, unknown>} doc
 */
function ensureSlugOnDoc(slugHint, doc) {
  if (!doc || typeof doc !== "object") return doc;
  const s = String(doc.slug ?? "").trim();
  if (s) return doc;
  const h = String(slugHint ?? "").trim();
  if (!h) return doc;
  return { ...doc, slug: h };
}

/**
 * Minimal list-row document when full `textLinked` hydration fails (list pages still need id/slug/level).
 *
 * @param {string} id
 * @param {string} listSlug
 * @param {unknown} listTitle
 * @param {unknown} listLevel
 */
function listRowToStubDoc(id, listSlug, listTitle, listLevel) {
  const contentId = String(id || "").trim();
  const slug = String(listSlug || "").trim();
  /** @type {Record<string, unknown>} */
  const stub = { contentId };
  if (slug) stub.slug = slug;
  const title = listTitle != null ? String(listTitle).trim() : "";
  if (title) stub.title = title;
  const level = listLevel != null ? String(listLevel).trim() : "";
  if (level) stub.level = level;
  return ensureSlugOnDoc(slug, stub);
}

/**
 * One merged JSON row for `data/json` / SSR caches (detail document from Content API + list hints).
 *
 * @param {string} origin
 * @param {string} locale
 * @param {string} type
 * @param {string} id
 * @param {{ slug?: string; title?: string; level?: string }} [listItem]
 * @param {{ voice?: string }} [opts]
 */
export async function fetchRunningJsonForContentItem(origin, locale, type, id, listItem = {}, opts = {}) {
  const contentId = String(id ?? "").trim();
  if (!contentId) {
    throw new Error(`fetchRunningJsonForContentItem: missing content id (locale=${locale}, type=${type})`);
  }

  const slugHint = String(listItem.slug ?? "").trim();
  const listTitle = listItem.title;
  const listLevel = listItem.level;

  const full = await fetchContentFull(origin, locale, contentId, opts);
  let doc = deriveRunningJson(full);
  doc = ensureSlugOnDoc(slugHint, doc);

  const dTitle = /** @type {{ title?: unknown }} */ (doc).title;
  if (listTitle != null && String(listTitle).trim() !== "") {
    if (dTitle === undefined || dTitle === null || String(dTitle).trim() === "") {
      doc = { ...doc, title: listTitle };
    }
  }
  const dLevel = /** @type {{ level?: unknown }} */ (doc).level;
  if (listLevel != null && String(listLevel).trim() !== "") {
    if (dLevel === undefined || dLevel === null || String(dLevel).trim() === "") {
      doc = { ...doc, level: listLevel };
    }
  }

  return doc;
}

/**
 * @param {string} origin
 */
async function hydrateInterfaceMeta(origin) {
  const localesPayload = await fetchJsonOptional(`${origin}/v1/interface-meta/locales`);
  const locales = Array.isArray(localesPayload?.locales)
    ? localesPayload.locales.map((x) => String(x || "").toLowerCase()).filter(Boolean)
    : [];
  if (!locales.length) return;

  await poolMap(
    locales.flatMap((uiLang) =>
      INTERFACE_META_SECTIONS.map((section) => ({ uiLang, section })),
    ),
    8,
    async ({ uiLang, section }) => {
      try {
        const data = await fetchJsonOptional(
          `${origin}/v1/interface-meta/${encodeURIComponent(uiLang)}/${encodeURIComponent(section)}`,
        );
        if (!data) return;
        const items = Array.isArray(data.items) ? data.items : [];
        const bucket = interfaceMetaBucket(uiLang, section);
        for (const raw of items) {
          const row = /** @type {Record<string, unknown>} */ (raw);
          const contentId = String(row.contentId ?? "").trim();
          if (!contentId) continue;
          bucket.set(contentId, row);
        }
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        console.warn(`[content-api] interface-meta skip ${uiLang}/${section}: ${msg}`);
      }
    },
  );
}

async function runHydrate() {
  const origin = contentApiOrigin();
  if (!origin) return;

  /** @type {{ locale: string; type: string; id: string; listSlug: string; listTitle?: string; listLevel?: string }[]} */
  const jobs = [];

  for (const locale of FETCH_LOCALES) {
    for (const type of CONTENT_TYPES) {
      let list = [];
      try {
        list = await fetchListAllPages(origin, locale, type);
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        console.warn(`[content-api] list skip ${locale}/${type}: ${msg}`);
        continue;
      }
      for (const it of list) {
        const jid = String(it.id ?? it.contentId ?? "").trim();
        if (!jid) continue;
        jobs.push({
          locale,
          type,
          id: jid,
          listSlug: String(it.slug ?? "").trim(),
          listTitle: it.title,
          listLevel: it.level,
        });
      }
    }
  }

  await Promise.all([
    poolMap(jobs, 10, async ({ locale, type, id, listSlug, listTitle, listLevel }) => {
      try {
        let doc = await fetchRunningJsonForContentItem(origin, locale, type, id, {
          slug: listSlug,
          title: listTitle,
          level: listLevel,
        });
        doc = ensureSlugOnDoc(listSlug, doc);
        typeBucket(locale, type).set(id, doc);
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        console.warn(`[content-api] stub ${locale}/${type}/${id} (detail fetch failed): ${msg}`);
        typeBucket(locale, type).set(
          id,
          listRowToStubDoc(id, listSlug, listTitle, listLevel),
        );
      }
    }),
    hydrateInterfaceMeta(origin),
  ]);
}

/**
 * Fetches all list rows and full documents from the Content API once per process.
 * Safe to call multiple times.
 */
export async function ensureContentApiHydrated() {
  if (!isContentApiRuntimeEnabled()) return;
  if (hydrated) return;
  if (!hydratePromise) {
    hydratePromise = runHydrate()
      .then(() => {
        hydrated = true;
      })
      .catch((e) => {
        hydratePromise = null;
        throw e;
      });
  }
  await hydratePromise;
}

/**
 * @param {string} locale
 * @param {string} type
 * @returns {Record<string, unknown>[]}
 */
export function apiListDocsSorted(locale, type) {
  const bucket = typeBucket(locale, type);
  const out = Array.from(bucket.values());
  out.sort((a, b) =>
    String(/** @type {{ contentId?: string }} */ (a).contentId || "").localeCompare(
      String(/** @type {{ contentId?: string }} */ (b).contentId || ""),
    ),
  );
  return out;
}

/**
 * @param {string} locale
 * @param {string} type
 * @param {string} id
 */
export function apiGetDocById(locale, type, id) {
  const bid = String(id || "").trim();
  if (!bid) return null;
  return typeBucket(locale, type).get(bid) || null;
}

/**
 * UI-language meta overlay (title / teaser / description) for list and detail pages.
 * @param {string} uiLang
 * @param {string} section — stories|dialogs|scenes|adventures
 * @param {string} contentId
 */
export function apiGetInterfaceMeta(uiLang, section, contentId) {
  const lang = String(uiLang || "").toLowerCase();
  const sec = String(section || "").toLowerCase();
  const id = String(contentId || "").trim();
  if (!lang || !sec || !id) return null;
  return byInterfaceMeta.get(lang)?.get(sec)?.get(id) || null;
}

/** Drop in-memory Content API hydration (dev reload / SoT or exercise JSON edits). */
export function clearContentApiCaches() {
  byLocaleTypeId.clear();
  byInterfaceMeta.clear();
  hydratePromise = null;
  hydrated = false;
}
